import * as React from 'react';

import { DEMO } from '@/runtime-config';
import { DemoGuide } from './chrome/guide';
import { MOCK_ACCESS, holds, type MockScreen } from './roles';
import { HelpContactPanel } from './screens/help-contact';
import { CriteriaThresholdsPanel } from './screens/setup';
import { DemoStateProvider, useDemoOptional } from './state/demo-state';

/**
 * Where the demo touches a live screen, one export per place — each nothing at all with the demo
 * off, so a live screen reads the same in production as it did before the demo existed.
 *
 * A mock that extends a live screen is its own badged panel, section or tab inside it (the design's
 * §8): Help's contact block, the in-consideration thresholds on the concern criteria, and the
 * rail's mock entries. The live screen decides where it goes; the demo decides what it is. The
 * hub's Rankings & research was one until the advisor record page's F6 drew survey-api's
 * placements inside Veritant research and deleted it, the Roles tab beside Users until SR-228 made
 * Team Portal roles live, and the account's email-change request until SR-416 made the change live.
 */

/** The demo's state and its guide, around the whole app — or the app alone. */
export function DemoRoot({ children }: { children: React.ReactNode }) {
  if (!DEMO) return <>{children}</>;
  return (
    <DemoStateProvider>
      {children}
      <DemoGuide />
    </DemoStateProvider>
  );
}

export function HelpContactSlot({ signedIn }: { signedIn: boolean }) {
  return DEMO ? <HelpContactPanel signedIn={signedIn} /> : null;
}

/** The concern criteria's mock panel: the in-consideration thresholds, which have no route. */
export function CriteriaThresholdsSlot() {
  return DEMO ? <CriteriaThresholdsPanel /> : null;
}

/**
 * Whether a mock entry is drawn for the role being viewed as — the rail's half of "View as role".
 * Every entry is, with the demo off (none has a screen to hide) or while viewing as signed in.
 */
export function useMockOpens(): (screen: MockScreen) => boolean {
  const context = useDemoOptional();
  const viewAs = context?.state.viewAs ?? null;
  return React.useCallback(
    (screen: MockScreen) => {
      if (!viewAs) return true;
      const opens = MOCK_ACCESS[screen].opens;
      return opens === null || holds(viewAs, opens);
    },
    [viewAs]
  );
}
